import isUndefined from 'lodash/isUndefined';
import { useMemo } from 'react';
import { gray } from '../../../../components/Color';
import FlexGrid, { FlexGridColumn } from '../../../../components/FlexGrid';
import { MovingGradient } from '../../../../components/MovingGradient';
import PaddingBox from '../../../../components/PaddingBox';
import Typography from '../../../../components/Typography';
import { codes } from '../../../../data/translations';
import { isBadOrEmptyString } from '../../../../predicates/string';
import { CustomObject } from '../../../../types';
import { isMobileAny } from '../../../../utils/device';
import { getWindowUrlParams } from '../../../../utils/url';
import { useTranslationAPI } from '../../../translations';
import { ListFilterBreadcrumbCollapseProvider } from '../../components/ListFilterBreadcrumb/ListFilterBreadcrumbCollapseProvider';
import { ListFilterBreadcrumbTemp } from '../../components/ListFilterBreadcrumb/ListFilterBreadcrumbTemp';
import {
	ListLayoutBar,
	ListLayoutBarItem,
	ListLayoutBarLeft,
	ListLayoutBarRight,
	ListLayoutBarRow,
	ListLayoutTitle,
} from '../../components/ListLayout';
import { ListLoadingSortByTooltipButton } from './ListLoadingSortByTooltipButton';

function useComputeListName({ title, titleCode }: Pick<ListLoadingPlaceholderBarProps, 'title' | 'titleCode'>) {
	const { translate } = useTranslationAPI();

	if (!isBadOrEmptyString(title)) {
		return title;
	}

	if (!isUndefined(titleCode)) {
		return translate(titleCode);
	}

	return (
		<MovingGradient
			height='24px'
			width='120px'
		/>
	);
}

type ListLoadingPlaceholderBarProps = {
	title?: string;
	titleCode?: number;
	skipTotalItemsCount?: boolean;
	hasSorting?: boolean;
};

function ListLoadingPlaceholderBar({
	title,
	titleCode,
	skipTotalItemsCount,
	hasSorting,
}: ListLoadingPlaceholderBarProps) {
	const { translate } = useTranslationAPI();
	const listName = useComputeListName({ title, titleCode });
	const showTempBreadcrumb = useMemo(() => {
		if (isMobileAny) {
			return false;
		}

		const urlParams = getWindowUrlParams() as CustomObject;

		return Object.keys(urlParams).length > 0;
	}, []);
	const itemsCountElement = (
		<FlexGrid>
			<FlexGridColumn>
				<PaddingBox rightScanty>
					<Typography
						fontSize='14px'
						color={gray[6]}
					>
						{translate(codes.common.itemsCountIndicator)}
					</Typography>
				</PaddingBox>
			</FlexGridColumn>
			<FlexGridColumn>
				<MovingGradient
					height='16px'
					width='32px'
					style={{
						marginLeft: '4px',
					}}
				/>
			</FlexGridColumn>
		</FlexGrid>
	);
	const hasSortingOrItemsCount = hasSorting || !skipTotalItemsCount;

	return (
		<ListLayoutBar>
			<ListLayoutBarRow>
				<ListLayoutBarLeft>
					<ListLayoutBarItem>
						<ListLayoutTitle>{listName}</ListLayoutTitle>
					</ListLayoutBarItem>
				</ListLayoutBarLeft>
			</ListLayoutBarRow>
			{isMobileAny && hasSortingOrItemsCount && (
				<ListLayoutBarRow>
					{hasSorting && (
						<ListLayoutBarLeft>
							<ListLayoutBarItem>
								<ListLoadingSortByTooltipButton />
							</ListLayoutBarItem>
						</ListLayoutBarLeft>
					)}
					{!skipTotalItemsCount && (
						<ListLayoutBarRight>
							<ListLayoutBarItem>{itemsCountElement}</ListLayoutBarItem>
						</ListLayoutBarRight>
					)}
				</ListLayoutBarRow>
			)}
			{!isMobileAny && (
				<>
					{!skipTotalItemsCount && (
						<ListLayoutBarRow>
							<ListLayoutBarLeft>
								<ListLayoutBarItem>
									<FlexGrid center>
										<FlexGridColumn>{itemsCountElement}</FlexGridColumn>
									</FlexGrid>
								</ListLayoutBarItem>
							</ListLayoutBarLeft>
						</ListLayoutBarRow>
					)}
					{hasSorting && (
						<ListLayoutBarRow>
							<ListLayoutBarLeft>
								<ListLayoutBarItem>
									<ListLoadingSortByTooltipButton />
								</ListLayoutBarItem>
							</ListLayoutBarLeft>
						</ListLayoutBarRow>
					)}
				</>
			)}

			{showTempBreadcrumb && (
				<ListLayoutBarRow>
					<ListFilterBreadcrumbCollapseProvider>
						<ListFilterBreadcrumbTemp />
					</ListFilterBreadcrumbCollapseProvider>
				</ListLayoutBarRow>
			)}
		</ListLayoutBar>
	);
}

export { ListLoadingPlaceholderBar };
